import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { getAllocationSettingKey } from './billEditUtils';
import { findBillProperty, resolveUtilityTarget } from './resolveAllocationTargets';

/**
 * The properties the bill was uploaded for. One of them names an extracted block outright only when
 * the extraction found a single address; against several addresses it would label every block the
 * same, so the addresses are matched instead. Several uploaded properties are always worth keeping:
 * they narrow which properties an address can match to the ones the uploader named.
 */
function getAssignedPropertyIds(extractedProperties, billPropertyIds) {
  const assigned = (billPropertyIds || []).filter(Boolean);
  return assigned.length > 1 || extractedProperties.length === 1 ? assigned : [];
}

/**
 * Reads the extraction against the utilities the properties are set up with. Every service the bill
 * charges for is answered with the property utility it belongs to, and with whether the property is
 * set up for it at all: bills routinely cover services the company does not allocate, and those are
 * kept apart rather than shown as something to bill residents for.
 *
 * A service whose property is still unknown counts as covered until proven otherwise, as does one
 * whose property the user picked themselves, so nothing disappears in answer to a choice just made.
 */
export function matchBillUtilities(_ref) {
  let billPropertyIds = _ref.billPropertyIds,
    data = _ref.data,
    properties = _ref.properties,
    targetOverrides = _ref.targetOverrides;
  const extractedProperties = (data === null || data === void 0 ? void 0 : data.properties) || [];
  const assignedPropertyIds = getAssignedPropertyIds(extractedProperties, billPropertyIds);
  return extractedProperties.map((extracted, propertyIndex) => {
    const address = extracted.address || '';
    // The property and utility ids are stamped onto the bill by the services that validated it, from
    // the same address and utility types read here. Trusting them keeps the two answers in step, and
    // an unvalidated bill has none, so the reading below still has to work on its own.
    const extractedPropertyId = extracted.propertyId || '';
    // Matching an address means reading every property the company has, and every line of this block
    // is billed for the same one, so it is worked out once and only redone for a line pointed
    // somewhere else by hand.
    const matched = findBillProperty({
      address,
      assignedPropertyIds,
      extractedPropertyId,
      properties
    });
    return {
      address,
      propertyIndex,
      services: (extracted.services || []).map((service, serviceIndex) => {
        const key = getAllocationSettingKey(propertyIndex, service.serviceType);
        const override = targetOverrides === null || targetOverrides === void 0 ? void 0 : targetOverrides[key];
        const property = override ? findBillProperty({
          address,
          assignedPropertyIds,
          extractedPropertyId,
          override,
          properties
        }) : matched;
        const target = resolveUtilityTarget({
          extractedUtilityId: service.utilityId || '',
          override,
          property,
          // A bill names the provider per service, but often only once at the top of the page.
          providerName: service.provider || (data === null || data === void 0 ? void 0 : data.vendorName) || '',
          serviceType: service.serviceType
        });
        return {
          isCovered: target.needsProperty || !!override || target.hasUtilityMatch,
          key,
          service,
          serviceIndex,
          target
        };
      })
    };
  });
}

/** One side of the split, dropping the properties that have nothing left on that side. */
export function selectServices(groups, isCovered) {
  return groups.map(group => _objectSpread(_objectSpread({}, group), {}, {
    services: group.services.filter(item => item.isCovered === isCovered)
  })).filter(group => group.services.length > 0);
}

/** The utilities these groups charge for, in the order the extraction lists them. */
export function getServiceTypes(groups) {
  const types = new Set();
  groups.forEach(group => group.services.forEach(_ref2 => {
    let service = _ref2.service;
    if (service !== null && service !== void 0 && service.serviceType) types.add(service.serviceType);
  }));
  return [...types];
}
export function getServiceIndexes(group) {
  return group.services.map(item => item.serviceIndex);
}